package link.erpango.quip.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Card
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Text
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.List
import androidx.compose.material.icons.filled.Share
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import link.erpango.quip.theme.QuipColors
import org.jetbrains.compose.resources.stringResource
import quip.composeapp.generated.resources.Res
import quip.composeapp.generated.resources.*
import compose.icons.FontAwesomeIcons
import compose.icons.fontawesomeicons.Solid
import compose.icons.fontawesomeicons.solid.Check
import compose.icons.fontawesomeicons.solid.CloudUploadAlt
import compose.icons.fontawesomeicons.solid.Sync
import link.erpango.quip.utils.format

enum class SyncStatus {
    SYNCED,
    LOCAL_ONLY,
    REMOTE_ONLY
}


/**
 * Shared generic document item component.
 */
@Composable
fun QuipDocumentItem(
    name: String,
    fileType: String,
    filePath: String,
    sizeMb: Double,
    dateFormatted: String,
    syncStatus: SyncStatus = SyncStatus.SYNCED,
    onShare: () -> Unit,
    onDelete: () -> Unit,
    onClick: () -> Unit,
    onLongClick: () -> Unit,
    onUpload: (() -> Unit)? = null,
    onDownload: (() -> Unit)? = null,
    onEnsureLocal: (() -> Unit)? = null // Optional callback to ensure file is local
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .pointerInput(Unit) {
                detectTapGestures(
                    onTap = { onClick() },
                    onLongPress = { onLongClick() }
                )
            },
        backgroundColor = QuipColors.colorSurface,
        shape = RoundedCornerShape(8.dp),
        elevation = 0.dp
    ) {
        Row(
            modifier = Modifier
                .padding(12.dp)
                .fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Thumbnail
            Box(
                modifier = Modifier
                    .size(50.dp)
                    .clip(RoundedCornerShape(8.dp))
                    .background(QuipColors.colorPrimary.copy(alpha = 0.1f)),
                contentAlignment = Alignment.Center
            ) {
                val isImage = fileType.lowercase() in listOf("jpg", "jpeg", "png", "image/jpeg")
                
                if (isImage && filePath.isNotEmpty()) {
                    AsyncImage(
                        model = filePath,
                        contentDescription = null,
                        contentScale = ContentScale.Crop,
                        modifier = Modifier.fillMaxSize()
                    )
                } else {
                    if (isImage && filePath.isEmpty() && onEnsureLocal != null) {
                         LaunchedEffect(Unit) {
                             onEnsureLocal()
                         }
                    }
                    
                    // Fallback icon
                    Icon(
                        Icons.Default.List, // Or a more specific file icon if available in generalized way
                        contentDescription = null,
                        tint = QuipColors.colorPrimary
                    )
                }
            }
            
            Spacer(modifier = Modifier.width(12.dp))
            
            // Info
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = name,
                    style = MaterialTheme.typography.subtitle1,
                    color = QuipColors.colorText,
                    maxLines = 1
                )
                Text(
                    text = buildString {
                        append(dateFormatted)
                        if (sizeMb > 0) {
                            append(" • ")
                            append(sizeMb.format(2))
                            append(" MB")
                        }
                    },
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorTextSecondary
                )
            }
            
            // Actions
            
             // Sync Status / Action
            when (syncStatus) {
                SyncStatus.LOCAL_ONLY -> {
                    IconButton(onClick = { onUpload?.invoke() }) {
                        Icon(
                            FontAwesomeIcons.Solid.CloudUploadAlt,
                            contentDescription = "Upload",
                            tint = QuipColors.colorWarning,
                            modifier = Modifier.size(24.dp)
                        )
                    }
                }
                SyncStatus.REMOTE_ONLY -> {
                    IconButton(onClick = { onDownload?.invoke() }) {
                        Icon(
                            FontAwesomeIcons.Solid.Sync,
                            contentDescription = "Download",
                            tint = QuipColors.colorPrimary,
                            modifier = Modifier.size(24.dp)
                        )
                    }
                }
                SyncStatus.SYNCED -> {
                     Icon(
                         FontAwesomeIcons.Solid.Check,
                        contentDescription = "Synced",
                        tint = QuipColors.colorSuccess,
                        modifier = Modifier.padding(8.dp).size(24.dp)
                    )
                }
            }

            if (syncStatus != SyncStatus.REMOTE_ONLY) {
                IconButton(onClick = onShare) {
                    Icon(
                        Icons.Default.Share,
                        contentDescription = "Share",
                        tint = QuipColors.colorPrimary
                    )
                }
            }
            
            IconButton(onClick = onDelete) {
                Icon(
                    Icons.Default.Delete,
                    contentDescription = stringResource(Res.string.delete_document),
                    tint = QuipColors.colorError.copy(alpha = 0.7f)
                )
            }
        }
    }
}

